import { Alert, AlertDescription } from "@screener/uikit/components/alert";
import { Ago } from "@/density/elapsed";
import { formatClockMinutes } from "@/density/format";
import type { StreamState } from "@/density/stream-state";
import type { StreamStore } from "@/density/use-density-stream";
import { useStreamSelector } from "@/density/use-stream-selector";
import { exchangeLabel, isMarketKind, marketLabel } from "@/lib/labels";

function selectMarketsDown(state: StreamState): ReadonlyMap<string, number> {
  return state.marketsDown;
}

/** Ключ `StreamState.marketsDown` («binance:perp») обратно в биржу и рынок. */
function venueLabel(key: string): string {
  const separator = key.indexOf(":");
  const exchange = key.slice(0, separator);
  const market = key.slice(separator + 1);
  return `${exchangeLabel(exchange)} · ${isMarketKind(market) ? marketLabel(market) : market}`;
}

/**
 * Плашка недоступных рынков (§132 п. 3): одна на все экраны, строка на рынок —
 * «Binance · PERP недоступна с 14:32 UTC» и время с этого момента. Пока
 * недоступных рынков нет, видимого нет — только пустая живая область. Экранный компонент, а не UIKit:
 * знает рынок и момент из состояния потока.
 */
export function UnavailableMarketsAlert({
  marketsDown,
}: {
  marketsDown: ReadonlyMap<string, number>;
}) {
  // Живая область смонтирована всегда, меняется только содержимое: регион,
  // вставленный в DOM сразу с текстом, объявляется ненадёжно (review
  // stage5-round-price, п. 2; тот же приём — в `connection-status.tsx`).
  // Роль `status` у `Alert` отключена: объявляет обёртка, не обе сразу.
  return (
    <div aria-live="polite" className="shrink-0 empty:hidden">
      {marketsDown.size === 0 ? null : (
        // biome-ignore lint/a11y/useValidAriaRole: undefined снимает встроенную роль `status` — объявляет обёртка
        <Alert
          role={undefined}
          data-unavailable-markets
          className="items-center rounded-none border-x-0 border-t-0 px-4 py-1.5 text-xs"
        >
          <AlertDescription className="flex flex-col gap-0.5">
            {[...marketsDown].map(([key, sinceMs]) => (
              <p key={key} data-market-down={key}>
                {venueLabel(key)} недоступна с {formatClockMinutes(sinceMs)}
                {/* Объявляется только «недоступна с HH:MM UTC»: давность тикает
                    каждую секунду (как в `connection-status.tsx`). */}
                <span aria-hidden="true" className="tabular-nums">
                  {" "}
                  (<Ago tsMs={sinceMs} /> назад)
                </span>
              </p>
            ))}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/** Плашка над экраном: читает карту из потока, перерисовывается только её сменой. */
export function UnavailableMarketsBanner({ store }: { store: StreamStore }) {
  const marketsDown = useStreamSelector(store, selectMarketsDown);
  return <UnavailableMarketsAlert marketsDown={marketsDown} />;
}
